import { useCallback, useContext, useEffect, useState } from "react"
import _ from "lodash"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//GraphQl
import {
  GET_KIOSK_STAFF_LIST,
  GET_KIOSK_TEMPLATES_ORGANIZATION,
  GET_TEMPLATE_FIELDS,
} from "../../queries"
import { useLazyQuery } from "@apollo/client"

//Kiosk components
import WizardPageProfile from "./WizardPageProfile"
import WizardPageFields from "./WizardPageFields"
import WizardPageSubmitActions from "./WizardPageActions"
import {
  kioskApiPostCreateForm,
  kioskApiPutUpdateForm,
  kioskApiGetFormProfile,
  kioskApiGetFormExclusions,
} from "../../kioskApi"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Steps } from "primereact/steps"
import { Button } from "primereact/button"

//SOAR Components
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"
import { LoaderMedium } from "../../../../components/Loaders"

//Styles
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import myStyles from "../../../../styles/kiosk/KioskAdminFormWizard.module.css"

export default function KioskAdminFormWizard({
  onClose,
  onSubmit,
  templates,
  formId,
  kioskOrganizationId,
  organizationId,
}) {
  const userCtx = useContext(UserContext)

  const newAction = {
    id: undefined,
    formId: undefined,
    name: "Default",
    sendSignalR: false,
    sendEmail: false,
    notifyOrganizationAdmin: false,
    notifyEnterpriseAdmin: false,
    notificationBody: "",
    otherEmailRecipients: "",
    emailSubject: "",
    emailBody: "",
    active: true,
  }

  //Global isLoading state
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  //Template list to use & template loading indicator status
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true)
  const [exclusionConfig, setExclusionConfig] = useState(undefined)
  const [templateList, setTemplateList] = useState([])
  const [staffList, setStaffList] = useState([])

  //Loading status for selected Template field
  const [isLoadingFields, setIsLoadingFields] = useState(true)
  const [loadingFieldStatus, setLoadingFieldStatus] = useState("")

  const [templateFieldsLoaded, setTemplateFieldsLoaded] = useState("")
  const [formProfile, setFormProfile] = useState({
    id: undefined,
    kioskOrganizationId: kioskOrganizationId,
    name: "",
    soarTemplateName: "",
    soarIntakeType: "participant",
    active: true,
    userId: userCtx.session.user.staff_id,
    fields: [],
    submitActions: [newAction],
  })
  const [pageIndex, setPageIndex] = useState(0)
  const stepItems = [
    {
      label: "Create Form",
    },
    {
      label: "Customization",
    },
    {
      label: "Post Submit",
    },
  ]

  const [loadFields] = useLazyQuery(GET_TEMPLATE_FIELDS, {
    fetchPolicy: "cache-and-network",
    variables: {
      organization_id: userCtx.session.user.organization_id,
      template_name: "",
    },
  })
  const [loadSOARTemplates] = useLazyQuery(GET_KIOSK_TEMPLATES_ORGANIZATION, {
    fetchPolicy: "cache-and-network",
    variables: {
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const [loadSOARStaff] = useLazyQuery(GET_KIOSK_STAFF_LIST, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const loadFormProfile = useCallback(async () => {
    try {
      if (formId) {
        const result = await kioskApiGetFormProfile(
          userCtx.tokens.getKioskToken(),
          formId,
        )

        if (result) {
          //handle instances where there might not be submitActions for some reason.
          if (result.submitActions?.length === 0) {
            result.submitActions = [{ ...newAction, formId: result.id }]
          }
          setFormProfile({ ...result, userId: userCtx.session.user.staff_id })
          setIsLoading(false)
        } else {
          LogError("Unable to load Kiosk form", result)
          userCtx.setNotificationError("Unable to load Kiosk form")
          onClose()
        }
      } else {
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load Kiosk form", error)
      userCtx.setNotificationError("Unable to load Kiosk form")
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId])

  useEffect(() => {
    kioskApiGetFormExclusions(userCtx.tokens.getKioskToken())
      .then((result) => {
        if (result) {
          setExclusionConfig(result)
          setIsLoadingConfig(false)
        } else {
          LogError("field exclusions not found", result)
          userCtx.setNotificationError(
            "Unable to load Kiosk form configuration",
          )
          onClose()
        }
      })
      .catch((error) => {
        LogError("Unable to load field exclusions", error)
        userCtx.setNotificationError("Unable to load Kiosk form configuration")
        onClose()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadFormProfile()
  }, [loadFormProfile])

  const loadStaffList = useCallback(async () => {
    try {
      const staffEntries = await loadSOARStaff({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          organization_id: organizationId,
        },
      })

      if (staffEntries.data.staffsByOrganization) {
        const staffEntriesUpdated = staffEntries.data.staffsByOrganization.map(
          (staff) => {
            return {
              ...staff,
              staff_full_name: `${staff.staff_name_first} ${staff.staff_name_last}`,
            }
          },
        )

        setStaffList(staffEntriesUpdated)
      } else {
        setStaffList([])
      }
    } catch (error) {
      LogError("Unable to load staff list", error)
      setStaffList([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId])

  useEffect(() => {
    loadStaffList()
  }, [loadStaffList])

  const loadTemplates = useCallback(async () => {
    try {
      if (!templates) {
        const soarTemplates = await loadSOARTemplates({
          variables: {
            organization_id: organizationId,
          },
        })
        if (soarTemplates?.data?.getKioskTemplates) {
          setTemplateList(soarTemplates?.data?.getKioskTemplates || [])
          setIsLoadingTemplate(false)
        } else {
          userCtx.setNotificationError("No templates found")
          LogError("No templates found", templateList)
          onClose()
        }
      } else {
        setTemplateList(templates)
        setIsLoadingTemplate(false)
      }
    } catch (error) {
      LogError("Unable to load SOAR templates", error)
      userCtx.setNotificationError("Unable to load template profiles")
      onclose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const loadTemplateFields = async (templateName, kioskFields, onSetFields) => {
    try {
      if (templateName !== templateFieldsLoaded) {
        setLoadingFieldStatus("Loading template fields")
        setIsLoadingFields(true)

        const fields = await loadFields({
          variables: {
            staff_id: userCtx.session.user.staff_id,
            organization_id: organizationId,
            template_name: templateName,
          },
        })

        if (fields.data?.template?.blocks) {
          setLoadingFieldStatus("Processing template fields")
          let newFields = []

          if (
            templateName === templateFieldsLoaded ||
            templateFieldsLoaded === ""
          ) {
            newFields = _.cloneDeep(kioskFields) || []
          }

          let templateFields = new Set()

          const excludedBlocks = exclusionConfig?.blockIds
            ? exclusionConfig.blockIds
            : []

          const excludedFieldTypes = exclusionConfig?.fieldTypes
            ? exclusionConfig.fieldTypes
            : []

          const excludedFields = exclusionConfig?.fieldIds
            ? exclusionConfig.fieldIds
            : []

          const orderedBlocks = [...fields.data?.template?.blocks]
          orderedBlocks.sort((a, b) => a.index - b.index)
          orderedBlocks
            .filter((b) => !excludedBlocks.includes(b.block_name))
            .forEach((block) => {
              block.fields
                .filter(
                  (field) =>
                    !excludedFieldTypes.includes(field.field_type) &&
                    !excludedFields.includes(field.field_name) &&
                    field.field_visibility,
                )
                .forEach((field) => {
                  //log template field to later check if DB fields should be removed
                  templateFields.add(`${block.block_name}_${field.field_name}`)

                  let required = field.system_required
                  if (
                    !required &&
                    field.field_validation?.required &&
                    field.field_validation?.required.length > 0
                  ) {
                    required = field.field_validation.required[0] === "true"
                  }

                  let newField = newFields.find(
                    (newField) =>
                      newField.blockId === block.block_name &&
                      newField.fieldId === field.field_name,
                  )

                  if (!newField) {
                    newField = {
                      id: undefined,
                      blockId: block.block_name,
                      blockName: block.block_title,
                      fieldId: field.field_name,
                      fieldLabel: field.field_label || field.field_id,
                      show: !!field.field_visibility,
                      required:
                        !!field.system_required ||
                        !!field.field_validation?.required[0],
                      systemRequired: field.system_required,
                      fieldType: field.field_type,
                      field_conditional: field?.field_conditional,

                      modified: false,
                      isTopLevel: true,
                    }

                    newFields.push(newField)
                  } else {
                    const indexOf = newFields.indexOf(newField)
                    newField.systemRequired = field.system_required
                    newField.blockName = block.block_title || newField.blockId
                    newField.fieldLabel = field.field_label || newField.fieldId
                    newField.fieldType = field.field_type
                    newField.field_conditional = field.field_conditional
                    newField.isTopLevel = true
                    newField.removed = false
                    newFields[indexOf] = newField
                  }

                  //If the field has a conditional parent, process the parent
                  if (
                    !_.isEmpty(newField?.field_conditional) &&
                    _.isString(newField?.field_conditional)
                  ) {
                    //Parse the conditional object if it exists
                    let conditionalValue = undefined
                    let conditionalParent = undefined

                    try {
                      conditionalValue = JSON.parse(field.field_conditional)
                      conditionalParent = Object.keys(conditionalValue)
                    } catch (error) {
                      LogError("Failed to parse field_conditional", error)
                    }

                    //Find the index before we change values
                    const childIndex = newFields.indexOf(newField)

                    //If the field has a parent, find or create it.
                    if (conditionalParent && !_.isEmpty(conditionalParent)) {
                      newField.conditionalParentId = conditionalParent[0]
                      newField.isTopLevel = false
                      newField.parentFieldId = conditionalParent[0]
                      newFields[childIndex] = newField

                      let parent = newFields.find(
                        (f) =>
                          f.blockId === block.block_name &&
                          f.fieldId === conditionalParent[0],
                      )

                      if (!parent) {
                        parent = {
                          id: undefined,
                          blockId: block.block_name,
                          blockName: block.block_title,
                          fieldId: conditionalParent[0],
                          fieldLabel: conditionalParent[0],
                          modified: false,
                          children: [newField],
                        }
                        newFields.push(parent)
                      } else {
                        if (parent.children) {
                          parent.children.push(newField)
                        } else {
                          parent.children = [newField]
                        }
                      }
                    }
                  }
                })
            })

          // Iterate newFields once to identity and mark removed fields as removed
          newFields = newFields.map((newField) => {
            if (!newField.removed) {
              // Only process if not already marked
              const key = `${newField.blockId}_${newField.fieldId}`
              if (!templateFields.has(key)) {
                // Field is in newFields (from DB/previous state) but not in current template
                return { ...newField, removed: true }
              }
            }
            return newField
          })

          const orderedFields = newFields
            .map((f) => {
              let updatedField = { ...f }
              const block = orderedBlocks.find(
                (b) => b.block_name === f.blockId, //f.blockId is actually block_name from template fields, this is a naming inconsistency that should be resolved in the future
              )
              if (block) {
                updatedField.index = block.index
              } else {
                // Fields for blocks no longer in the template won't be found.
                // Assign a high index to sort them to the end and prevent unstable sorting.
                updatedField.index = Number.MAX_SAFE_INTEGER
              }

              return updatedField
            })
            .sort((a, b) => a.index - b.index)

          onSetFields(orderedFields)
          setTemplateFieldsLoaded(templateName)
        } else {
          LogError("Unable to load template fields", fields)
          userCtx.setNotificationError("Unble to load template fields")
          setPageIndex(0)
        }
      }

      setIsLoadingFields(false)
    } catch (error) {
      LogError("Unable to load template fields", error)
      userCtx.setNotificationError("Unble to load template fields")
      setPageIndex(0)
      setIsLoadingFields(false)
    }
  }

  const handleSubmit = async (values) => {
    try {
      let useValues = _.cloneDeep(values)
      //validate post submit actions
      useValues.submitActions.forEach((action) => {
        //Keep double bang to ensure a boolean value is returned
        action.sendEmail = !!(
          action.otherEmailRecipients &&
          (action.emailBody || action.emailSubject)
        )
        action.sendSignalR =
          action.notifyEnterpriseAdmin || action.notifyOrganizationAdmin
      })

      //Cleanup fields for more efficient payload
      useValues.fieldNodes = undefined
      useValues.fields = useValues.fields.map((field) => {
        return {
          id: field.id,
          blockId: field.blockId,
          fieldId: field.fieldId,
          show: field.show,
          required: field.required,
          removed: field.removed,
        }
      })

      let result = undefined
      if (!useValues.id) {
        result = await kioskApiPostCreateForm(
          userCtx.tokens.getKioskToken(),
          useValues,
        )
      } else {
        result = await kioskApiPutUpdateForm(
          userCtx.tokens.getKioskToken(),
          useValues,
        )
      }

      onSubmit(result)
      return true
    } catch (error) {
      LogError("Unable to submit KioskForm profile to service", error)
      userCtx.setNotificationError(
        "Unable to save changes to the Kiosk service",
      )
      return false
    }
  }

  Yup.addMethod(Yup.array, "checkEmailRecipients", function (message) {
    return this.test("checkEmailRecipients", "", function (list) {
      const errors = []
      const propertyPath = "otherEmailRecipients"

      list.forEach((item, index) => {
        const emailSchema = Yup.array().of(Yup.string().email())
        let recipients = _.get(item, propertyPath)

        if (recipients) {
          recipients = recipients.split(";")

          if (!emailSchema.isValidSync(recipients)) {
            errors.push(
              this.createError({
                path: `${this.path}[${index}].${propertyPath}`,
                message,
              }),
            )
          }
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Please enter a name for this form"),
    soarTemplateName: Yup.string().required("Please select a template"),
    submitActions: Yup.array()
      .min(1, "Please add at least one PDF document")
      .checkEmailRecipients("Only valid e-mails are accepted"),
  })
  return (
    <Dialog
      showHeader={false}
      visible={true}
      onHide={onClose}
      className={myStyles.dialog}
    >
      <div className={kioskStyles.modalHeader}>
        {"Kiosk Form"}
        <Button
          icon="pi pi-times"
          className={kioskStyles.hideModalButton}
          onClick={() => {
            onClose()
          }}
        />
      </div>
      <div
        className={`${kioskStyles.modalFormContainer} ${myStyles.formContainer}`}
      >
        <hr className={kioskStyles.modalHeaderLine} />
        {isLoading || isLoadingTemplate || isLoadingConfig ? (
          <LoaderMedium />
        ) : (
          <div className={myStyles.Layout}>
            <div
              className={`${myStyles.listLayoutSteps} ${kioskStyles.verticalSteps}`}
            >
              <Steps model={stepItems} activeIndex={pageIndex} />
            </div>
            <div className={myStyles.listLayoutPage}>
              <Formik
                initialValues={formProfile}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (values, { resetForm }) => {
                  if (await handleSubmit(values)) {
                    resetForm({ values })
                  }
                }}
              >
                {({
                  handleSubmit,
                  isSubmitting,
                  dirty,
                  values,
                  setFieldValue,
                }) => (
                  <Form
                    onSubmit={handleSubmit}
                    autoComplete="off"
                    style={{ height: "CALC( 100% - 20px)" }}
                  >
                    {pageIndex === 0 ? (
                      <WizardPageProfile
                        templates={templateList}
                        onCancel={onClose}
                        onNext={() => {
                          loadTemplateFields(
                            values.soarTemplateName,
                            values.fields,
                            (newFields) => {
                              setFieldValue("fields", newFields)

                              const GetNode = (field, parent) => {
                                let childNodes = []

                                if (field.children) {
                                  childNodes = field.children.map(
                                    (childField) => {
                                      return GetNode(childField, field)
                                    },
                                  )
                                }

                                return {
                                  key:
                                    (parent?.blockId || "") +
                                    (parent?.fieldId || "") +
                                    (parent ? "|" : "") +
                                    field.blockId +
                                    field.fieldId,
                                  label: field.fieldLabel,
                                  data: {
                                    fieldLabel: field.fieldLabel,
                                    blockId: field.blockId,
                                    fieldId: field.fieldId,
                                    parentBlockId: parent?.blockId,
                                    parentFieldId: parent?.fieldId,
                                    hasParent: !!parent,
                                    blockName: field.blockName,
                                    systemRequired: field.systemRequired,
                                  },
                                  children: childNodes || [],
                                }
                              }

                              const nodes = newFields
                                .filter((field) => field.isTopLevel)
                                .map((field) => {
                                  return GetNode(field)
                                })

                              setFieldValue("fieldNodes", nodes)
                            },
                          )
                          setPageIndex(1)
                        }}
                      />
                    ) : null}
                    {isLoadingFields && pageIndex === 1 ? (
                      <div className={myStyles.templateLoaderContainer}>
                        <div
                          className={myStyles.templateLoaderMessageContainer}
                        >
                          <p className={myStyles.templateLoaderMessage}>
                            {loadingFieldStatus}
                          </p>
                          <LoaderMedium />
                        </div>
                      </div>
                    ) : null}
                    {pageIndex === 1 && !isLoadingFields ? (
                      <>
                        <WizardPageFields
                          onBack={() => {
                            setPageIndex(0)
                          }}
                          onNext={() => {
                            setPageIndex(2)
                          }}
                        />
                      </>
                    ) : null}
                    {pageIndex === 2 ? (
                      <WizardPageSubmitActions
                        staffList={staffList}
                        onBack={() => {
                          setPageIndex(1)
                        }}
                        onCancel={onClose}
                        isSubmitting={isSubmitting}
                      />
                    ) : null}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
